import { useState } from 'react';
import Navbar from '../../components/Navbar';

interface Question {
  id: number;
  author: string;
  avatar: string;
  time: string;
  category: string;
  title: string;
  content: string;
  answers: number;
  views: number;
  votes: number;
  isAnswered: boolean;
  fullAnswers?: Answer[];
}

interface Answer {
  id: number;
  author: string;
  avatar: string;
  time: string;
  content: string;
  votes: number;
  isBestAnswer: boolean;
}

export default function QAPage() {
  const [selectedQuestion, setSelectedQuestion] = useState<Question | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [askModalOpen, setAskModalOpen] = useState(false);

  const questions: Question[] = [
    {
      id: 1,
      author: 'Jennifer Martinez',
      avatar: 'https://public.readdy.ai/ai/img_res/2b6d862debfa9285ddfc4067755ece61.jpg',
      time: '2 hours ago',
      category: 'Parking',
      title: 'How do I get a guest parking permit?',
      content: 'My family is visiting next week and they will need parking. What is the process to get a guest parking permit? How long does it take?',
      answers: 3,
      views: 45,
      votes: 8,
      isAnswered: true,
      fullAnswers: [
        {
          id: 1,
          author: 'Board Admin',
          avatar: 'https://readdy.ai/api/search-image?query=professional%20friendly%20person%20smiling%20headshot%20portrait%20warm%20lighting%20simple%20clean%20background&width=100&height=100&seq=qa-admin-1&orientation=squarish',
          time: '1 hour ago',
          content: 'You can request guest parking permits through the resident portal or by contacting the HOA office. Permits are typically issued within 24 hours. Each household can have up to 2 guest permits active at a time, valid for up to 7 days each.',
          votes: 12,
          isBestAnswer: true
        },
        {
          id: 2,
          author: 'Mike Thompson',
          avatar: 'https://readdy.ai/api/search-image?query=professional%20friendly%20man%20smiling%20headshot%20portrait%20warm%20lighting%20simple%20clean%20background&width=100&height=100&seq=qa-avatar-2&orientation=squarish',
          time: '45 minutes ago',
          content: 'I just got mine last week. Super easy process - just fill out the online form and you get an email confirmation within a few hours!',
          votes: 5,
          isBestAnswer: false
        }
      ]
    },
    {
      id: 2,
      author: 'David Chen',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20friendly%20man%20smiling%20headshot%20portrait%20warm%20lighting%20simple%20clean%20background%20confident&width=100&height=100&seq=qa-avatar-3&orientation=squarish',
      time: '5 hours ago',
      category: 'Amenities',
      title: 'What are the pool hours for summer?',
      content: 'I noticed the pool schedule changed. Can someone confirm the new summer hours? Also, are there any restrictions for children?',
      answers: 2,
      views: 67,
      votes: 12,
      isAnswered: true,
      fullAnswers: [
        {
          id: 1,
          author: 'Sarah Johnson',
          avatar: 'https://readdy.ai/api/search-image?query=professional%20friendly%20woman%20smiling%20headshot%20portrait%20warm%20lighting%20simple%20clean%20background&width=100&height=100&seq=qa-avatar-4&orientation=squarish',
          time: '3 hours ago',
          content: 'Summer pool hours are 6 AM to 10 PM daily. Children under 14 must be accompanied by an adult. Lap swim is from 6-8 AM on weekdays.',
          votes: 15,
          isBestAnswer: true
        }
      ]
    },
    {
      id: 3,
      author: 'Emily Rodriguez',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20friendly%20woman%20smiling%20headshot%20portrait%20warm%20lighting%20simple%20clean%20background%20kind&width=100&height=100&seq=qa-avatar-5&orientation=squarish',
      time: '1 day ago',
      category: 'Pets',
      title: 'Are there any restrictions on dog breeds?',
      content: 'We are considering adopting a larger dog breed. Does the HOA have any breed restrictions or size limits for pets?',
      answers: 4,
      views: 89,
      votes: 15,
      isAnswered: true
    },
    {
      id: 4,
      author: 'Robert Williams',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20friendly%20man%20smiling%20headshot%20portrait%20warm%20lighting%20simple%20clean%20background%20trustworthy&width=100&height=100&seq=qa-avatar-6&orientation=squarish',
      time: '1 day ago',
      category: 'Maintenance',
      title: 'How do I report a broken streetlight?',
      content: 'There is a streetlight out on Oak Avenue that has been dark for a few days. What is the proper way to report this?',
      answers: 1,
      views: 34,
      votes: 6,
      isAnswered: true
    },
    {
      id: 5,
      author: 'Lisa Anderson',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20friendly%20woman%20smiling%20headshot%20portrait%20warm%20lighting%20simple%20clean%20background%20cheerful&width=100&height=100&seq=qa-avatar-7&orientation=squarish',
      time: '2 days ago',
      category: 'Amenities',
      title: 'Can I reserve the clubhouse for a birthday party?',
      content: 'I would like to host my daughter\'s birthday party at the clubhouse. What is the reservation process and are there any fees?',
      answers: 2,
      views: 56,
      votes: 9,
      isAnswered: true
    },
    {
      id: 6,
      author: 'James Taylor',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20friendly%20man%20smiling%20headshot%20portrait%20warm%20lighting%20simple%20clean%20background&width=100&height=100&seq=qa-avatar-8&orientation=squarish',
      time: '3 days ago',
      category: 'Rules',
      title: 'What are the quiet hours in the community?',
      content: 'I want to make sure I am respectful of my neighbors. What are the official quiet hours for the community?',
      answers: 3,
      views: 78,
      votes: 11,
      isAnswered: true
    }
  ];

  const categories = ['all', 'Parking', 'Amenities', 'Pets', 'Maintenance', 'Rules', 'Fees'];

  const filteredQuestions = questions.filter(q => {
    const matchesSearch = q.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         q.content.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || q.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-gray-50 font-['Inter',sans-serif]">
      <Navbar />

      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-3">Community Q&amp;A</h1>
          <p className="text-lg text-gray-600">
            Get answers to your questions about HOA policies, amenities, and community guidelines
          </p>
        </div>

        {/* Search and Actions */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-8">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 relative">
              <i className="ri-search-line absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-lg"></i>
              <input
                type="text"
                placeholder="Search questions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm"
              />
            </div>

            <button
              onClick={() => setAskModalOpen(true)}
              className="px-6 py-3 bg-teal-500 text-white rounded-xl font-medium hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2"
            >
              <i className="ri-question-answer-line text-xl"></i>
              Ask a Question
            </button>
          </div>

          {/* Category Filter */}
          <div className="flex gap-2 overflow-x-auto mt-4 pb-2">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap cursor-pointer transition-all text-sm ${
                  selectedCategory === category
                    ? 'bg-teal-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Questions List */}
          <div className="lg:col-span-2 space-y-6">
            {filteredQuestions.map((question) => (
              <div
                key={question.id}
                className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => setSelectedQuestion(question)}
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                    <img
                      src={question.avatar}
                      alt={question.author}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-gray-900">{question.author}</h3>
                      <span className="text-sm text-gray-500">{question.time}</span>
                    </div>
                    <span className="inline-block px-2 py-1 bg-teal-100 text-teal-700 text-xs rounded-full">
                      {question.category}
                    </span>
                  </div>
                  {question.isAnswered && (
                    <div className="flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                      <i className="ri-check-line"></i>
                      Answered
                    </div>
                  )}
                </div>

                <h2 className="text-xl font-bold text-gray-900 mb-2">{question.title}</h2>
                <p className="text-gray-700 mb-4 line-clamp-2">{question.content}</p>

                <div className="flex items-center gap-6 text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <i className="ri-chat-3-line"></i>
                    <span>{question.answers} answers</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="ri-eye-line"></i>
                    <span>{question.views} views</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="ri-arrow-up-line"></i>
                    <span>{question.votes} votes</span>
                  </div>
                </div>
              </div>
            ))}

            {filteredQuestions.length === 0 && (
              <div className="text-center py-16 bg-white rounded-2xl">
                <i className="ri-question-line text-6xl text-gray-300 mb-4"></i>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No questions found</h3>
                <p className="text-gray-600 mb-6">Try adjusting your search or be the first to ask!</p>
                <button
                  onClick={() => setAskModalOpen(true)}
                  className="px-6 py-3 bg-teal-500 text-white rounded-xl font-medium hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Ask a Question
                </button>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Stats */}
            <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl p-6 text-white">
              <h3 className="text-xl font-bold mb-4">Community Stats</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-teal-100">Total Questions</span>
                  <span className="text-2xl font-bold">247</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-teal-100">Answered</span>
                  <span className="text-2xl font-bold">231</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-teal-100">Active Users</span>
                  <span className="text-2xl font-bold">89</span>
                </div>
              </div>
            </div>

            {/* Popular Topics */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="font-bold text-gray-900 mb-4">Popular Topics</h3>
              <div className="space-y-3">
                {['Parking', 'Amenities', 'Pets', 'Maintenance', 'Rules'].map((topic, index) => (
                  <div key={topic} className="flex items-center justify-between">
                    <span className="text-gray-700">{topic}</span>
                    <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                      {[45, 38, 29, 24, 18][index]}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Ask CTA */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6">
              <i className="ri-lightbulb-line text-4xl text-purple-600 mb-3"></i>
              <h3 className="font-bold text-gray-900 mb-2">Have a Question?</h3>
              <p className="text-sm text-gray-600 mb-4">
                Don't hesitate to ask! Our community is here to help.
              </p>
              <button
                onClick={() => setAskModalOpen(true)}
                className="w-full px-4 py-3 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Ask Now
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Question Detail Modal */}
      {selectedQuestion && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-6"
          onClick={() => setSelectedQuestion(null)}
        >
          <div
            className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-8">
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-start gap-4 flex-1">
                  <div className="w-14 h-14 rounded-full overflow-hidden flex-shrink-0">
                    <img
                      src={selectedQuestion.avatar}
                      alt={selectedQuestion.author}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{selectedQuestion.author}</h3>
                    <p className="text-sm text-gray-500">{selectedQuestion.time}</p>
                    <span className="inline-block px-2 py-1 bg-teal-100 text-teal-700 text-xs rounded-full mt-2">
                      {selectedQuestion.category}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedQuestion(null)}
                  className="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors cursor-pointer flex-shrink-0"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">{selectedQuestion.title}</h2>
              <p className="text-gray-700 mb-6 leading-relaxed">{selectedQuestion.content}</p>

              <div className="flex items-center gap-6 text-sm text-gray-600 mb-8 pb-8 border-b border-gray-200">
                <button className="flex items-center gap-2 hover:text-teal-600 transition-colors cursor-pointer">
                  <i className="ri-arrow-up-line"></i>
                  <span>{selectedQuestion.votes}</span>
                </button>
                <div className="flex items-center gap-2">
                  <i className="ri-eye-line"></i>
                  <span>{selectedQuestion.views} views</span>
                </div>
                <button className="flex items-center gap-2 hover:text-teal-600 transition-colors cursor-pointer">
                  <i className="ri-share-line"></i>
                  <span>Share</span>
                </button>
              </div>

              {/* Answers */}
              <div className="space-y-6">
                <h3 className="text-xl font-bold text-gray-900">
                  {selectedQuestion.answers} {selectedQuestion.answers === 1 ? 'Answer' : 'Answers'}
                </h3>

                {selectedQuestion.fullAnswers?.map((answer) => (
                  <div
                    key={answer.id}
                    className={`p-6 rounded-2xl ${
                      answer.isBestAnswer ? 'bg-green-50 border-2 border-green-200' : 'bg-gray-50'
                    }`}
                  >
                    {answer.isBestAnswer && (
                      <div className="flex items-center gap-2 text-green-700 font-medium mb-3">
                        <i className="ri-check-double-line"></i>
                        <span className="text-sm">Best Answer</span>
                      </div>
                    )}
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                        <img
                          src={answer.avatar}
                          alt={answer.author}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900">{answer.author}</h4>
                        <p className="text-sm text-gray-500">{answer.time}</p>
                      </div>
                    </div>
                    <p className="text-gray-700 leading-relaxed mb-4">{answer.content}</p>
                    <div className="flex items-center gap-4">
                      <button className="flex items-center gap-2 text-sm text-gray-600 hover:text-teal-600 transition-colors cursor-pointer">
                        <i className="ri-arrow-up-line"></i>
                        <span>{answer.votes}</span>
                      </button>
                      <button className="text-sm text-gray-600 hover:text-teal-600 transition-colors cursor-pointer">
                        Reply
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Answer Form */}
              <div className="mt-8 pt-8 border-t border-gray-200">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Your Answer</h3>
                <textarea
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm resize-none mb-4"
                  placeholder="Share your knowledge..."
                ></textarea>
                <button className="px-6 py-3 bg-teal-500 text-white rounded-xl font-medium hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer">
                  Post Answer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Ask Question Modal */}
      {askModalOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-6"
          onClick={() => setAskModalOpen(false)}
        >
          <div
            className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Ask a Question</h2>
              <button
                onClick={() => setAskModalOpen(false)}
                className="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Question Title
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm"
                  placeholder="What would you like to know?"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category
                </label>
                <select className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm">
                  <option>Parking</option>
                  <option>Amenities</option>
                  <option>Pets</option>
                  <option>Maintenance</option>
                  <option>Rules</option>
                  <option>Fees</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Details
                </label>
                <textarea
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm resize-none"
                  placeholder="Provide more details about your question..."
                ></textarea>
              </div>
            </div>

            <div className="flex gap-4 mt-6">
              <button
                onClick={() => setAskModalOpen(false)}
                className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                Cancel
              </button>
              <button className="flex-1 px-6 py-3 bg-teal-500 text-white rounded-xl font-medium hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer">
                Post Question
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
